import { ChangeFromCloseLabelComponent } from "../../components/change-from-close-label.component";
import { ComparisonSymbolsLastPriceComponent } from "../../components/comparison-symbols-last-price.component";
import { IComponent } from "../../components/component.interface";
import { DrawTradePriceAnnotationComponent } from "../../components/draw-trade-price-annotation.component";
import { DrawingToolsManager } from "../../components/drawing-tools/drawing-tools.manager";
import { HighChartAnnotationPopupComponent } from "../../components/highchart-annotation-popup.component";
import { IndicatorLastPriceComponent } from "../../components/indicators-last-price.component";
import { LastPriceComponent } from "../../components/last-price.component";
import { TiChartSeriesComponent } from "../../components/ti-chart-series.component";
import { TooltipComponent } from "../../components/tooltip.component";
import { DomEventsSubscribe } from "../../events/dom-events-subscribe";
import { TraceLoggingHelpers } from "../../helpers/trace-logging-helpers";
import { UrlHelpers } from "../../helpers/url-helpers";
import { RequestService } from "../../services/request.service";
import { TiChartMenuContainer } from "../../ti-chart-menu.container";
import { ChartStyleColorModel } from "../chart-style/chart-style-color.model";
import { ChartStyleSettingsModel } from "../chart-style/chart-style-settings.model";
import { TIChartPointModel } from "../data-point/ti-chart-point.model";
import { ScrollMode } from "../enum/scroll-mode.enum";
import { Timeframe } from "../enum/timeframe.enum";
import { IChartContainer } from "./chart-container.interface";
import { IChartDataService } from "./chart-data-service.interface";
const color = require('../../library/darken-color.js');
declare const window: any;

export class BaseChartContainer implements IChartContainer {
    public chartService: IChartDataService = null as any;
    public requestService = new RequestService();
    public domEvents = new DomEventsSubscribe();
    public drawingToolsManager: DrawingToolsManager | null = null;

    public components: Array<IComponent> = [];
    public body = document.body;

    public marketingWebChartMode = false;
    public pnpMode = false;
    public isPreview = false;
    public isDemo = false;
    public isLiveData = false;
    public isTouchDevice = false;
    public scrollingEnabled = true;
    public scrollMode = ScrollMode.MouseWheel;
    public id = '';
    public index = 0;
    public basePath = '';

    public tiChartSeries: TiChartSeriesComponent | null = null;
    public tiChartMenu: TiChartMenuContainer | null = null;
    public tiChartAnnotationPopup: HighChartAnnotationPopupComponent | null = null;
    public chartWrapper: HTMLElement | null = null as any;
    public element: HTMLElement = null as any;

    public changeFromClosePipChartFirstBarClose = 0;
    public changeFromClosePipChartFirstBarX = 0;
    public changeFromClosePipChartLastBarClose = 0;
    public changeFromClosePipChartLastBarX = 0;
    public changeFromClosePercentagePipChart = 0;

    private changePositive = false;

    constructor() {
        if ('ontouchstart' in window || navigator.maxTouchPoints) {
            this.isTouchDevice = true;
        }
    }

    public getComponentById(id: string): IComponent {
        for (let i = 0; i < this.components.length; i++) {
            if (this.components[i].id === id) {
                return this.components[i];
            }
        }
        return null!;
    }

    public getComponentsByType(type: any): Array<IComponent> {
        const results: Array<IComponent> = [];
        for (let i = 0; i < this.components.length; i++) {
            if (this.components[i] instanceof type) {
                results.push(this.components[i]);
            }
        }
        return results;
    }

    public getComponentsByTypes(types: Array<any>): Array<IComponent> {
        const results: Array<IComponent> = [];
        for (let i = 0; i < this.components.length; i++) {
            for (var j = 0; j < types.length; j++) {
                if (this.components[i] instanceof types[j]) {
                    results.push(this.components[i]);
                }
            }
        }

        return results;
    }

    public getPnPSwitchElement(): HTMLElement {
        return document.body.findFirst('.pnp-switch');
    }

    public isExtraSmall(): boolean {
        return this.element.parentElement?.classList.contains('extra-small') ?? false;
    }
 
    /**
     * 
     * @returns true if the body size is valid, false if the body size is too small (5x5).
     */
    public isBodySizeValid(): boolean {
        const width = document.body.clientWidth;
        const height = document.body.clientHeight;
        if (width <= 5 && height <= 5) {
            return false;
        }

        return true;
    }

    public setCurrentFirstAndLastPointChangesFromCloseColor(lastPrice: number, min?: number, max?: number) {
        let firstClose: number | null = null;
        let lastClose: number | null = null;
        let firstXValue: number | null = null;
        let lastXValue: number | null = null;

        if (!isNaN(min) && !isNaN(max)) {
            firstXValue = this.tiChartSeries.tiChartPoints?.[min]?.xValue ?? null;
            lastXValue = this.tiChartSeries.tiChartPoints?.[max]?.xValue ?? null;
        } else {
            const mainChartSerie: any = this.tiChartSeries.highStockChart?.series?.[0];
            if (!mainChartSerie?.processedXData?.length) {
                return;
            }

            for (let i = 0; i < mainChartSerie.processedXData.length; i++) {
                const targetPoint = mainChartSerie.points.find((x: any) => x?.x == mainChartSerie.processedXData[i]);
                if (targetPoint) {
                    if (this.pnpMode) {
                        if (targetPoint.isInside) {
                            firstXValue = mainChartSerie.processedXData[i];
                            break;
                        }
                    } else {
                        if (targetPoint.graphic?.element) {
                            const elementRect =this.element.getBoundingClientRect();
                            const svgRect = targetPoint.graphic.element.getBoundingClientRect();
                            const isVisible = (
                                svgRect.bottom > 0 &&
                                svgRect.left > elementRect.left - (svgRect.width / 2)
                            );
                            if (isVisible) {
                                firstXValue = mainChartSerie.processedXData[i];
                                break;
                            }
                        }
                    }
                }
            }

            for (let i = mainChartSerie.processedXData.length - 1; i >= 0; i--) {
                const targetPoint = mainChartSerie.points.find((x: any) => x?.x == mainChartSerie.processedXData[i]);
                if (targetPoint) {
                    if (this.pnpMode) {
                        if (targetPoint.isInside) {
                            lastXValue = mainChartSerie.processedXData[i];
                            break;
                        }
                    } else {
                        if (targetPoint.graphic?.element) {
                            const elementRect = this.element.getBoundingClientRect();
                            const svgRect = targetPoint.graphic.element.getBoundingClientRect();
                            const isVisible = (
                                svgRect.bottom > 0 &&
                                svgRect.left > elementRect.left - (svgRect.width / 2)
                            );
                            if (isVisible) {
                                lastXValue = mainChartSerie.processedXData[i];
                                break;
                            }
                        }
                    }
                }
            }  
        }

        if (firstXValue == null || lastXValue == null) {
            return;
        }

        const isLastBar = this.tiChartSeries.lastDataPoint?.xValue == lastXValue;
        if (isLastBar) {
            lastClose = lastPrice;
        } else {
            const lastPoint = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == lastXValue);
            lastClose = lastPoint?.close ?? null;
        }

        const firstPoint = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == firstXValue);
        firstClose = firstPoint?.close ?? null;

        if (lastClose == null || firstClose == null) { return; }

        this.changeFromClosePipChartFirstBarClose = firstClose;
        this.changeFromClosePipChartFirstBarX = firstXValue;
        this.changeFromClosePipChartLastBarClose = lastClose;
        this.changeFromClosePipChartLastBarX = lastXValue;

        this.calculateChangeFromCloseForChart();

        if (this.pnpMode) {
            this.setPipChartStyleWithChangesFromClose(this.tiChartSeries.chartStyleSettings);
        } else {
            this.updateChangeFromCloseComponent();
        }
    }

    public calculateChangeFromCloseForChart() {
        if (!this.changeFromClosePipChartLastBarClose) {
            return;
        }

        this.changePositive = this.changeFromClosePipChartLastBarClose >= this.changeFromClosePipChartFirstBarClose;
        if (this.changePositive) {
            // if the change is positive, the Change Percent can go above 100%, i.e., if the starting price is $1.38 and the current price is $24.05, then the Change Percnet will be 1500%+
            this.changeFromClosePercentagePipChart = ((this.changeFromClosePipChartLastBarClose - this.changeFromClosePipChartFirstBarClose) / this.changeFromClosePipChartFirstBarClose) * 100
        }
        else {
            // if the change is negative, the Change Percent should be 0 to 100%
            this.changeFromClosePercentagePipChart = ((this.changeFromClosePipChartFirstBarClose - this.changeFromClosePipChartLastBarClose) / this.changeFromClosePipChartFirstBarClose) * 100
        }
    }

    public setPipChartStyleWithChangesFromClose(chartStyleModel: ChartStyleSettingsModel) {
        if (!chartStyleModel) { return; }

        let colorHex = this.getPiPSelectedLineColor(chartStyleModel);

        chartStyleModel.selectedLineColor = new ChartStyleColorModel(color.HEX2RGB(colorHex));
        let colorGredientOne = '';
        let colorGredientTwo = '';
        if (this.marketingWebChartMode) {
            if (this.changePositive) {
                if (chartStyleModel.isDarkTheme) {
                    chartStyleModel.marketingChartAreaFillHexColor = new ChartStyleColorModel(color.HEX2RGB('#00ff01'));

                    colorGredientOne = chartStyleModel.marketingChartAreaFillHexColor.toRGB();
                    colorGredientTwo = chartStyleModel.marketingChartAreaFillHexColor.toRGBA(0.3);
                } else {
                    chartStyleModel.marketingChartAreaFillHexColor = new ChartStyleColorModel(color.HEX2RGB('#00ff01'));
                    
                    colorGredientOne = chartStyleModel.marketingChartAreaFillHexColor.toRGB();
                    colorGredientTwo = chartStyleModel.marketingChartAreaFillHexColor.toRGBA(0.4);
                }
            } else {
                if (chartStyleModel.isDarkTheme) {
                    chartStyleModel.marketingChartAreaFillHexColor = new ChartStyleColorModel(color.HEX2RGB('#ff0005'));
                    
                    colorGredientOne = chartStyleModel.marketingChartAreaFillHexColor.toRGB();
                    colorGredientTwo = chartStyleModel.marketingChartAreaFillHexColor.toRGBA(0.3);
                } else {
                    chartStyleModel.marketingChartAreaFillHexColor = new ChartStyleColorModel(color.HEX2RGB('#fe0400'));
                    
                    colorGredientOne = chartStyleModel.marketingChartAreaFillHexColor.toRGB();
                    colorGredientTwo = chartStyleModel.marketingChartAreaFillHexColor.toRGBA(0.4);
                }
            }

            chartStyleModel.isGradientAreaColorChecked = true;
            this.tiChartSeries.highChartStyleBuilder.createMainChartStyle(chartStyleModel);
    
            const highChartRoot = this.element.querySelector('.highcharts-root defs');
            if (highChartRoot) {
                const styleDef = highChartRoot.querySelector('#tichart-bg-pnp-' + this.id);
                if (styleDef) {
                    styleDef.remove();
                }
    
                const bgDefTags = this.tiChartSeries.highChartStyleBuilder.getTIChartBackgroundSVGDefs(chartStyleModel);
                if (bgDefTags) {
                    highChartRoot.appendChild(bgDefTags);
                }
    
                const styleAreaDef = highChartRoot.querySelector('#tichart-area-bg-pnp-' + this.id);
                if (styleAreaDef) {
                    styleAreaDef.remove();
                }
    
                const bgAreaDefTags = this.tiChartSeries.highChartStyleBuilder.getMarketingWebChartPIPTIChartAreaBackgroundSVGDefs(chartStyleModel, colorGredientOne, colorGredientTwo);
                if (bgAreaDefTags) {
                    highChartRoot.appendChild(bgAreaDefTags);
                }
            }

        } else {
            chartStyleModel.isOneColor = true;

            this.tiChartSeries.highChartStyleBuilder.createMainChartStyle(chartStyleModel);
    
            const highChartRoot = this.element.querySelector('.highcharts-root defs');
            if (highChartRoot) {
                const styleDef = highChartRoot.querySelector('#tichart-bg-pnp-' + this.id);
                if (styleDef) {
                    styleDef.remove();
                }
    
                const bgDefTags = this.tiChartSeries.highChartStyleBuilder.getTIChartBackgroundSVGDefs(chartStyleModel);
                if (bgDefTags) {
                    highChartRoot.appendChild(bgDefTags);
                }
    
                const styleAreaDef = highChartRoot.querySelector('#tichart-area-bg-pnp-' + this.id);
                if (styleAreaDef) {
                    styleAreaDef.remove();
                }
    
                const bgAreaDefTags = this.tiChartSeries.highChartStyleBuilder.getTIChartAreaBackgroundSVGDefs(chartStyleModel);
                if (bgAreaDefTags) {
                    highChartRoot.appendChild(bgAreaDefTags);
                }
            }
        }

        this.updateChangeFromCloseComponent();
    }

    private getPiPSelectedLineColor(chartStyleModel: ChartStyleSettingsModel) {
        let colorHex = '';
        if (this.changePositive) {
            colorHex = chartStyleModel.selectedUpCandleColor.toHex();
        } else {
            colorHex = chartStyleModel.selectedDownCandleColor.toHex();
        }
        return colorHex;
    }

    private updateChangeFromCloseComponent() {
        if (!this.pnpMode || this.tiChartSeries?.chartStyleSettings?.pnpChangePercentEnabled) {
            const ctx = this as any;
            let baseChartStyle = this.pnpMode ? ctx.baseChart?.tiChartSeries?.chartStyleSettings : this.tiChartSeries?.chartStyleSettings;
            
            let labelColorHex = '';
            if (this.changePositive) {
                labelColorHex = baseChartStyle?.getChangeFromCloseUpColor();
                if (!labelColorHex) {
                    labelColorHex = baseChartStyle?.selectedUpCandleColor?.toHex();
                }
            } else {
                labelColorHex = baseChartStyle?.getChangeFromCloseDownColor();
                if (!labelColorHex) {
                    labelColorHex = baseChartStyle?.selectedDownCandleColor?.toHex();
                }
            }
            
            const labelComponent = this.getComponentsByType(ChangeFromCloseLabelComponent)[0] as ChangeFromCloseLabelComponent;
            labelComponent?.update(
                this.changeFromClosePercentagePipChart,
                this.changePositive,
                labelColorHex,
                this.changeFromClosePipChartFirstBarX,
                this.changeFromClosePipChartLastBarX);
        }
    }

    public setLastPricePositions() {
        this.getComponentsByTypes([
            LastPriceComponent,
            IndicatorLastPriceComponent,
            ComparisonSymbolsLastPriceComponent,
            DrawTradePriceAnnotationComponent
        ]).forEach((i: IComponent) => {
            if (i instanceof LastPriceComponent) {
                const component = i as LastPriceComponent;
                component.setLastPriceLabelPosition();
            } else if (i instanceof IndicatorLastPriceComponent) {
                const component = i as IndicatorLastPriceComponent;
                component.setLastPriceLabelPosition();
            } else if (i instanceof ComparisonSymbolsLastPriceComponent) {
                const component = i as ComparisonSymbolsLastPriceComponent;
                component.setLastPriceLabelPosition();
            } else if (i instanceof DrawTradePriceAnnotationComponent) {
                const component = i as DrawTradePriceAnnotationComponent;
                component.setPosition();
            }
        });
    }

    public hideTooltip() {
        this.tiChartSeries?.highStockChart?.tooltip?.hide();

        const toolTip = this.getComponentsByType(TooltipComponent)[0] as TooltipComponent;
        toolTip?.hide();
    }

    public setComponentStateWhenRequestingNewSymbol(symbol: string, timeframe: Timeframe) {
    }

    public toggleChartToPreviewMode(preview: boolean) {
        this.isPreview = preview;
    }

    public handleTraceLogging(traceLogging?: boolean) {
        const traceLoggingIsSet = traceLogging != undefined && traceLogging != null;
        if (traceLoggingIsSet) {
            new TraceLoggingHelpers(this, traceLogging);
            return;
        }

        // const envMode = process.env['NODE_ENV'];
        // const isProduction = envMode.toLowerCase() == 'production';
        const isTIPro = !this.marketingWebChartMode;
        if (isTIPro) {
            new TraceLoggingHelpers(this, false);
            return;
        }

        // const envMode = process.env['NODE_ENV'];
        // const isProduction = envMode.toLowerCase() != 'production';
        // TraceLoggingHelpers.log(`envMode: ${envMode}`);

        // if (envMode.toLowerCase() != 'production') {
        //     new TraceLoggingHelpers(this, false);
        //     return;
        // }

        const traceLoggingEnabledSessionKey = 'TraceLoggingEnabled';
        const traceLoggingEnabledValue = UrlHelpers.getQueryString('TraceLoggingEnabled');
        if (traceLoggingEnabledValue.toLowerCase() == 'true') {
            localStorage.setItem(traceLoggingEnabledSessionKey, 'true');
            new TraceLoggingHelpers(this, true);
            return;
        }

        const traceLoggingEnabledInSession = localStorage.getItem(traceLoggingEnabledSessionKey);
        new TraceLoggingHelpers(this, traceLoggingEnabledInSession?.toLowerCase() == 'true');
    }
}